import { createSelector, createStructuredSelector } from 'reselect'
import get from 'lodash/get'

const selectTutorial = state => state.tutorials

const selectTutorials = createSelector(
  selectTutorial,
  tutorials => tutorials.tutorials
)

const selectFrame = createSelector(
  selectTutorial,
  tutorials => tutorials.currentFrame
)

const selectCurrentTutorialId = createSelector(
  selectTutorial,
  tutorials => tutorials.currentTutorialId
)

const selectCurrentTutorial = createSelector(
  selectTutorial,
  tutorials => tutorials.currentTutorial
)

const selectCurrentPhoneTutorialId = createSelector(
  selectTutorial,
  tutorials => tutorials.currentPhoneId
)

const selectMostSeenTutorials = createSelector(
  selectTutorial,
  tutorials => tutorials.mostSeenTutorials
)

// List of tutorial categories; initId is the first tutorial in each category
const selectTutorialCategories = createSelector(
  selectTutorials,
  tutorialList =>
    tutorialList.map(category => {
      return { name: category.name, initId: category.children[0].pk }
    })
)

const selectCurrentCategory = createSelector(
  selectTutorials,
  selectCurrentTutorialId,
  (tutorialList, currentTutorialId) =>
    tutorialList.find(category =>
      category.children.find(tutorial => tutorial.pk === currentTutorialId)
    )
)

// List of tutorials from the currently selected category; initId is the tutorialId
const selectCurrentCategoryTutorials = createSelector(
  selectCurrentCategory,
  category =>
    category
      ? category.children.map(tutorial => {
        return {
          name: tutorial.name,
          initId: tutorial.pk,
          type: tutorial.tutorialtype
        }
      })
      : []
)

const selectCurrentCategoryName = createSelector(
  selectCurrentCategory,
  category => (category ? category.name : '')
)

const selectCurrentTutorialName = createSelector(
  selectCurrentCategoryTutorials,
  selectCurrentTutorialId,
  (tutorialList, currentTutorialId) =>
    tutorialList.length
      ? tutorialList.find(tutorial => tutorial.initId === currentTutorialId)
        .name
      : ''
)

const selectCurrentStep = createSelector(
  selectTutorial,
  tutorials => tutorials.currentStep
)

const selectIsLastStep = createSelector(
  selectTutorial,
  tutorials => tutorials.currentStep === tutorials.currentTutorial.length - 1
)

const selectCurrentTutorialSteps = createSelector(
  selectCurrentTutorial,
  tutorial =>
    tutorial &&
    tutorial
      .map((step, index) => {
        return (
          step.tutorialstep.outputOrder === 1 && {
            arrayLoc: index,
            text: step.tutorialstep.step
          }
        )
      })
      .filter(step => step)
)

const selectNextFullStep = createSelector(
  selectCurrentTutorialSteps,
  selectCurrentStep,
  (stepList, stepIndex) =>
    stepList.length &&
    (stepIndex === stepList[stepList.length - 1].arrayLoc
      ? 0
      : stepList.find(step => step.arrayLoc > stepIndex).arrayLoc)
)

const selectPrevFullStep = createSelector(
  selectCurrentTutorialSteps,
  selectCurrentStep,
  (stepList, stepIndex) =>
    stepList.length &&
    (stepIndex < stepList[1].arrayLoc
      ? 0
      : [...stepList].reverse().find(step => step.arrayLoc < stepIndex)
        .arrayLoc)
)

const selectAugmentedTutorialSteps = createSelector(
  selectCurrentTutorialSteps,
  selectCurrentStep,
  (stepList, stepIndex) =>
    stepList.length &&
    stepList.map((step, index) => {
      return {
        ...step,
        active:
          stepIndex >= step.arrayLoc &&
          stepIndex <
            (stepList[index + 1] ? stepList[index + 1].arrayLoc : 1000),
        completed:
          stepIndex > step.arrayLoc &&
          !(
            stepIndex >= step.arrayLoc &&
            stepIndex <
              (stepList[index + 1] ? stepList[index + 1].arrayLoc : 1000)
          ),
        last: index === stepList.length - 1
      }
    })
)

const selectCurrentTutorialType = createSelector(
  selectCurrentCategoryTutorials,
  selectCurrentTutorialId,
  (tutorialList, currentTutorialId) =>
    tutorialList.length &&
    tutorialList
      .find(tutorial => tutorial.initId === currentTutorialId)
      .type.toUpperCase()
)

const selectPhoneScreenImage = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  (stepList, stepIndex) => {
    return (
      stepList.length && {
        ...stepList[0].phoneScreenImage,
        original: stepList[stepIndex].phoneScreenImage
      }
    )
  }
)

const selectPackageFile = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  (stepList, stepIndex) => {
    return stepList.length && stepList[stepIndex].packageFile
  }
)

const selectLcdImage = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  selectFrame,
  (stepList, stepIndex, frame) => {
    return (
      stepList.length &&
      (frame > -1
        ? get(stepList[stepIndex], `tutorialstep.animationJson[${frame}].image`, '')
        : get(stepList[stepIndex], 'tutorialstep.imageLocation', ''))
    )
  }
)

const selectPortraitOrientation = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  (stepList, stepIndex) => {
    return (
      stepList.length &&
      stepList[stepIndex].tutorialstep.orientation.toLowerCase() !== 'landscape'
    )
  }
)

const selectIsPlaying = createSelector(
  selectTutorial,
  tutorials => tutorials.isPlayingStep || tutorials.isPlayingAll
)

const selectButtonData = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  (stepList, stepIndex) => {
    return (
      stepList.length && {
        lcdButton: stepList[stepIndex].tutorialstep.lcdButton,
        swipeOrAction: stepList[stepIndex].tutorialstep.swipeOrAction,
        xUpperLeft: stepList[stepIndex].buttonScreenImages[0].buttonXUpperLeft,
        yUpperLeft: stepList[stepIndex].buttonScreenImages[0].buttonYUpperLeft,
        xSize: stepList[stepIndex].buttonScreenImages[0].buttonXSize,
        ySize: stepList[stepIndex].buttonScreenImages[0].buttonYSize
      }
    )
  }
)

const selectPreloadImages = createSelector(
  selectCurrentTutorial,
  tutorial =>
    tutorial &&
    [].concat.apply(
      tutorial.map(step => {
        return step.tutorialstep.imageLocation
      }),
      tutorial.map(step => {
        return step.tutorialstep.animationJson.map(frame => frame.image)
      })
    )
)

const selectAnimationData = createSelector(
  selectCurrentTutorial,
  selectCurrentStep,
  (stepList, stepIndex) => {
    return stepList.length && stepList[stepIndex].tutorialstep.animationJson
  }
)

const structuredTutorialState = createStructuredSelector({
  infoBar: createStructuredSelector({
    categoryName: selectCurrentCategoryName,
    categories: selectTutorialCategories,
    tutorialName: selectCurrentTutorialName,
    categoryTutorials: selectCurrentCategoryTutorials
  }),
  tutorial: createStructuredSelector({
    id: selectCurrentTutorialId,
    type: selectCurrentTutorialType,
    phoneImage: selectPhoneScreenImage,
    packageFile: selectPackageFile,
    lcdImage: selectLcdImage,
    portrait: selectPortraitOrientation,
    imageList: selectPreloadImages,
    button: selectButtonData,
    isPlaying: selectIsPlaying,
    steps: createStructuredSelector({
      currentId: selectCurrentStep,
      stepList: selectAugmentedTutorialSteps,
      nextStepId: selectNextFullStep,
      prevStepId: selectPrevFullStep
    })
  })
})

const selectIsFetching = createSelector(
  selectTutorial,
  tutorials => tutorials.isFetching || tutorials.isFetchingSteps
)

export {
  selectTutorials,
  selectMostSeenTutorials,
  selectCurrentPhoneTutorialId,
  selectCurrentTutorial,
  selectCurrentTutorialId,
  structuredTutorialState,
  selectAnimationData,
  selectCurrentStep,
  selectButtonData,
  selectIsLastStep,
  selectCurrentTutorialSteps,
  selectIsFetching
}



// WEBPACK FOOTER //
// ./src/containers/Tutorials/selectors.js